import React, {FC} from "react";
import {Tag, Tooltip} from "antd";
import moment from "moment";
import {ColumnProps} from "antd/lib/table";
import {
    GetMollieRefundsQuery,
    MollieRefundStatus,
} from "../../../generated/graphql";
import {RenderAmount, renderBookingNumber} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import css from "../../../views/TableColumnRenderers/TableColumnRenderers.less";
import i18n from "../../../services/i18n";

// ────────────────────────────────────────────────────────────────────────────
// Phase 14 / Plan 14-03 (REFD-07, REFD-08)
// Column-Definitionen + Status-Tag für RefundsList.
// Analog PaymentsListColumns::useHandleColumns — aber deutlich schlanker,
// da Refunds read-only sind (kein Edit-Modal, kein Selection-Bulk-Action).
// ────────────────────────────────────────────────────────────────────────────

export type MollieRefundItem = GetMollieRefundsQuery["mollieRefunds"]["items"][0];

// Zusätzliche (computed) Spalten, die nicht direkt in der Query liegen
type ExtraColumns = {
    rowIndex: "rowIndex";
    bookingNumber: "bookingNumber";
    booker: "booker";
    bookerEmail: "bookerEmail";
    refundKind: "refundKind";
    adminUserEmail: "adminUserEmail";
    courseNumber: "courseNumber";
};

export type TableType = MollieRefundItem & ExtraColumns;

// Die Query selektiert seit E1 zusätzlich bookingType/canceled/invoice sowie
// course.allowLessonBooking — diese Felder landen erst mit dem nächsten
// `npm run build:types` in den generierten Types. Bis dahin wird das
// Booking-Objekt für den Link-Renderer lokal aufgeweitet.
export type BookingLinkFields = {
    bookingType?: string | null;
    canceled?: string | null;
    invoice?: {id: string} | null;
    courseLessons?: Array<{course?: {allowLessonBooking?: boolean | null} | null} | null> | null;
};

// ────────────────────────────────────────────────────────────────────────────
// RefundStatusTag — 3 Status, 3 Farben, i18n-Labels
// Analog MollieStatusTag in PaymentsListColumns.tsx (Z.1126-1187).
// ────────────────────────────────────────────────────────────────────────────

type StatusMapEntry = {color: string; label: () => string};

const statusMap: {[key: string]: StatusMapEntry} = {
    [MollieRefundStatus.Pending]: {
        color: "blue",
        label: () => i18n.containers.payments.Refunds.statusPending(),
    },
    [MollieRefundStatus.Processed]: {
        color: "green",
        label: () => i18n.containers.payments.Refunds.statusProcessed(),
    },
    [MollieRefundStatus.Failed]: {
        color: "red",
        label: () => i18n.containers.payments.Refunds.statusFailed(),
    },
};

export const RefundStatusTag: FC<{status: string; canceledAtProvider?: boolean | null}> = ({status, canceledAtProvider}) => {
    // Bei Mollie stornierte Refunds tragen intern `failed` — für die Schule ist das aber kein Fehler.
    if (canceledAtProvider) {
        return <Tag color="orange" style={{margin: 0}}>{i18n.containers.payments.Refunds.statusCanceledAtProvider()}</Tag>;
    }
    const entry = statusMap[status];
    if (!entry) {
        return <Tag style={{margin: 0}}>{status}</Tag>;
    }
    return (
        <Tag color={entry.color} style={{margin: 0}}>
            {entry.label()}
        </Tag>
    );
};

// ────────────────────────────────────────────────────────────────────────────
// Helpers für computed columns
// ────────────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────────
// Verwaiste Belege (Incident 05.09.2026)
//
// Seit `mollie_refund.scheduledPaymentId` auf ON DELETE SET NULL steht, überlebt
// der Erstattungsbeleg das Löschen seiner Buchung. `scheduledPayment` ist dann
// null, und die Zeile lebt von den Snapshot-Spalten, die beim Anlegen gefüllt
// wurden. Alle Helfer greifen deshalb zuerst auf die Live-Relation zu und fallen
// auf den Snapshot zurück — der Beleg bleibt lesbar, statt als leere Zeile mit
// einem Betrag dazustehen.
// ────────────────────────────────────────────────────────────────────────────

/** true = die Buchung zu diesem Beleg existiert nicht mehr. */
export const isOrphanedRefund = (record: TableType): boolean => !record.scheduledPayment;

export const getRefundKind = (record: TableType): "full" | "partial" => {
    // Ohne den Snapshot verglich eine Waise gegen 0 und wurde IMMER als "Voll"
    // ausgewiesen — eine Teilerstattung wäre damit still falsch etikettiert.
    const liveAmount = record.scheduledPayment?.paymentAmount;
    const spAmount = Number((liveAmount === null || liveAmount === undefined ? record.paymentAmountSnapshot : liveAmount) ?? 0);
    const refundAmount = Number(record.amount ?? 0);
    // Kein Ratenbetrag bekannt: lieber "Teil" als eine falsche Voll-Aussage auf einem Beleg.
    if (!spAmount) return "partial";
    return refundAmount >= spAmount ? "full" : "partial";
};

export const getBookerName = (record: TableType): string => {
    const booker = record.scheduledPayment?.booking?.attendee?.booker;
    // Bucher-Snapshot, NICHT der Teilnehmer: die Spalte heisst "Bucher", und die beiden
    // fallen nur bei erwachsenen Selbstbuchern zusammen.
    if (!booker) return record.bookerNameSnapshot ?? "";
    const fn = booker.firstname ?? "";
    const ln = booker.lastname ?? "";

    return `${fn} ${ln}`.trim();
};

export const getBookerEmail = (record: TableType): string => {
    return record.scheduledPayment?.booking?.attendee?.booker?.email ?? "";
};

export const getBookingNumber = (record: TableType): string => {
    const booking = record.scheduledPayment?.booking;
    if (!booking) return record.bookingNumberSnapshot ?? "";
    return `${booking.bookingNumberPrefix ?? ""}-${booking.bookingNumber ?? ""}`;
};

export const getCourseNumber = (record: TableType): string => {
    const lesson = record.scheduledPayment?.booking?.courseLessons?.[0];
    const live = lesson?.course?.prefixedCourseNumber;

    return live ? live : record.courseNumberSnapshot ?? "";
};

// ────────────────────────────────────────────────────────────────────────────
// useHandleColumns — Hook-Signatur analog PaymentsListColumns
// Kein State, keine Mutations — reine render-Funktion.
// ────────────────────────────────────────────────────────────────────────────

export const useHandleColumns = (
    columnsToShow: Array<Extract<keyof TableType, string>>,
): Array<ColumnProps<TableType>> => {
    const columns: Array<ColumnProps<TableType>> = [];

    const titles: {[key: string]: () => string} = {
        rowIndex: () => "#",
        createDate: () => i18n.containers.payments.Refunds.columnCreateDate(),
        bookingNumber: () => i18n.containers.payments.Refunds.columnBooking(),
        courseNumber: () => i18n.containers.payments.Refunds.columnCourseNumber(),
        booker: () => i18n.containers.payments.Refunds.columnBooker(),
        bookerEmail: () => i18n.containers.payments.Refunds.columnBookerEmail(),
        amount: () => i18n.containers.payments.Refunds.columnAmount(),
        refundKind: () => i18n.containers.payments.Refunds.columnRefundKind(),
        reason: () => i18n.containers.payments.Refunds.columnReason(),
        status: () => i18n.containers.payments.Refunds.columnStatus(),
        adminUserEmail: () => i18n.containers.payments.Refunds.columnAdminUser(),
    };

    columnsToShow.forEach((col) => {
        const column: ColumnProps<TableType> = {
            title: titles[col] ? titles[col]() : col,
            dataIndex: col,
            key: col,
        };

        switch (col) {
            case "rowIndex":
                column.render = (_text, _record, index) => `${index + 1}`;
                column.width = 50;
                break;
            case "createDate":
                column.render = (_text, record: TableType) =>
                    record.createDate ? moment(record.createDate).format("DD.MM.YYYY HH:mm") : "";
                column.sorter = true;
                column.width = 150;
                break;
            case "bookingNumber":
                column.width = 140;
                column.render = (_text, record: TableType) => {
                    const booking = record.scheduledPayment?.booking;
                    if (!booking) {
                        // Verwaister Beleg: die Buchungsnummer steht noch da, aber es gibt
                        // nichts mehr zum Verlinken. Der Hinweis erklärt, warum.
                        const snapshot = record.bookingNumberSnapshot;
                        return (
                            <Tooltip title={i18n.containers.payments.Refunds.bookingDeletedHint()}>
                                <span>{snapshot ? `${snapshot} ` : ""}({i18n.containers.payments.Refunds.bookingDeleted()})</span>
                            </Tooltip>
                        );
                    }
                    const extras = booking as unknown as BookingLinkFields;
                    return renderBookingNumber({
                        bookingId: booking.id,
                        bookingNumberPrefix: booking.bookingNumberPrefix ?? "",
                        bookingNumber: booking.bookingNumber ?? "",
                        bookingType: extras.bookingType ?? undefined,
                        className: css.primary,
                        canceled: Boolean(extras.canceled),
                        isSettled: Boolean(extras.invoice),
                        allowLessonBooking: Boolean(extras.courseLessons?.[0]?.course?.allowLessonBooking),
                    });
                };
                break;
            case "courseNumber":
                column.render = (_text, record: TableType) => getCourseNumber(record);
                break;
            case "booker":
                column.render = (_text, record: TableType) => getBookerName(record);
                break;
            case "bookerEmail":
                column.render = (_text, record: TableType) => getBookerEmail(record);
                break;
            case "amount":
                column.align = "right";
                column.render = (_text, record: TableType) => (
                    <RenderAmount amount={Number(record.amount ?? 0)} />
                );
                column.sorter = true;
                break;
            case "refundKind": {
                column.render = (_text, record: TableType) => {
                    const kind = getRefundKind(record);
                    return (
                        <Tag>
                            {kind === "full"
                                ? i18n.containers.payments.Refunds.kindFull()
                                : i18n.containers.payments.Refunds.kindPartial()}
                        </Tag>
                    );
                };
                column.width = 90;
                break;
            }
            case "reason":
                column.render = (_text, record: TableType) => {
                    const r = record.reason ?? "";
                    if (r.length <= 40) return r;
                    return (
                        <Tooltip title={r}>
                            <span>{r.substring(0, 37)}…</span>
                        </Tooltip>
                    );
                };
                break;
            case "status":
                column.render = (_text, record: TableType) => (
                    <RefundStatusTag status={record.status} canceledAtProvider={record.canceledAtProvider} />
                );
                column.sorter = true;
                column.width = 130;
                break;
            case "adminUserEmail":
                // Im aktuellen Schema liefert die Query nur adminUserId — E-Mail-
                // Auflösung ist Phase 16 AUDIT-01 Scope. Hier zeigen wir die ID
                // (gekürzt) als Identifikator. CSV-Export (refundValues.ts) liefert
                // ebenfalls die ID.
                column.render = (_text, record: TableType) => {
                    const id = record.adminUserId ?? "";
                    return id ? (
                        <Tooltip title={id}>
                            <span>{id.substring(0, 8)}…</span>
                        </Tooltip>
                    ) : "";
                };
                break;
            default:
                break;
        }

        columns.push(column);
    });

    return columns;
};
